import { useEffect, useState } from 'react';
import { getAnimeDetails } from '@/services/mediaApi';
import type { AnimeDetails } from '@/types/streaming';

interface UseAnimeMediaState {
  details: AnimeDetails | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads anime details from /api/media/anime/:malId. Mirrors `useMedia` but
 * for MAL-keyed anime (no season concept; flat episode count carried in
 * `details.episodes`). Returns null details until the first fetch completes.
 */
export function useAnimeMedia(malId: number | null): UseAnimeMediaState {
  const [details, setDetails] = useState<AnimeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (malId == null) {
      setDetails(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAnimeDetails(malId)
      .then((d) => {
        if (cancelled) return;
        setDetails(d);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });
    return (): void => { cancelled = true; };
  }, [malId]);

  return { details, loading, error };
}
